import { inject, Injectable } from '@angular/core';
import { ActionPerformed, PushNotifications, PushNotificationSchema, Token } from '@capacitor/push-notifications';
import { Observable, of, switchMap, take, tap } from 'rxjs';
import { NotificationsService } from '@notifications/infra';
import { MODAL_TOKEN, ModalProvider, PLATFORM_TOKEN, PlatformProvider, TOAST_TOKEN, ToastProvider } from '@core';
import { Router } from '@angular/router';
import { notificationRedirectUtil } from '../utils';
import { NotificationType, OnboardingNotificationTypes } from '../enums';
import { NewTrophyRecievedModalComponent } from '../../components/new-trophy-recieved-modal/new-trophy-recieved-modal.component';
import { NotificationPayload, PushNotificationDTO } from '../interfaces';
import { actionPushComponentsMapper } from '../mappers/action-push-components.mapper';
import { MarkNotificationAsClickedPathParams, MarkNotificationsAsReadBody, RegisterDeviceTokenBody } from '@core-http';

@Injectable()
export class NotificationsInitializer {
  private readonly notificationsService: NotificationsService = inject(NotificationsService);
  private readonly platformProvider: PlatformProvider = inject(PLATFORM_TOKEN);
  private readonly router: Router = inject(Router);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly toastProvider: ToastProvider = inject(TOAST_TOKEN);
  private isListenerAdded: boolean = false;

  init(): Observable<void> {
    if (!this.platformProvider.isNativeRun) return of(void 0);
    this.requestPermissions();
    this.registerNotification();
    this.pushNotificationReceived();
    this.pushNotificationActionPerformed();

    PushNotifications.addListener('registrationError', (error: any) => {
      console.error('Error on registration: ' + JSON.stringify(error));
    });

    return of(void 0);
  }

  private requestPermissions(): void {
    PushNotifications.requestPermissions().then((result) => {
      if (result.receive === 'granted') {
        PushNotifications.register();
      }
    });
  }

  private registerNotification(): void {
    if (!this.isListenerAdded) {
      PushNotifications.addListener('registration', (token: Token) => {
        const payload: RegisterDeviceTokenBody = { token: token.value };

        this.notificationsService
          .registerToken(payload)
          .pipe(
            take(1),
            tap(() => localStorage.setItem('FCM_Device_Token', token.value))
          )
          .subscribe(() => (this.isListenerAdded = true));
      });
    }
  }

  private pushNotificationReceived(): void {
    PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotificationSchema | PushNotificationDTO) => {
        const notificationData: NotificationPayload = notification?.data;

        if (notificationData?.notificationType === NotificationType.ChatNewMessage)
          return this.showToast('Otrzymałeś nową wiadomość');

        if (notificationData?.notificationType === NotificationType.NewTrophyReceived)
          return this.newTrophyRecieved(notificationData);

        if (OnboardingNotificationTypes.includes(notificationData?.notificationType)) {
          this.modalProvider.showModal$({
            component: actionPushComponentsMapper[notificationData?.notificationType],
            componentProps: { gameId: notificationData.gameId },
            cssClass: 'present-modal'
          });
        }

        return this.showToast('Otrzymałeś nowe powiadomienie');
      }
    );
  }

  private pushNotificationActionPerformed(): void {
    PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
      notificationRedirectUtil({
        type: notification.notification?.data?.notificationType,
        gameId: notification.notification?.data?.gameId,
        router: this.router,
        customId: notification.notification?.data?.customId,
        customRedirectUrl: notification.notification?.data?.customRedirectUrl,
        teamId: notification.notification?.data?.teamId,
        receiverUserId: notification.notification?.data?.receiverUserId,
        customType: notification.notification?.data?.customType,
        onCustomModalOpen: () => this.openCustomNotificationModal,
        modalProvider: this.modalProvider
      });

      PushNotifications.removeAllDeliveredNotifications();

      const pathParams: MarkNotificationAsClickedPathParams = {
        notificationId: notification.notification?.data?.notificationId
      };

      this.notificationsService.markAsClicked(pathParams).pipe(take(1)).subscribe();
    });
  }

  private openCustomNotificationModal(): void {}

  private newTrophyRecieved(data: NotificationPayload): void {
    this.modalProvider
      .showModal$({
        component: NewTrophyRecievedModalComponent,
        componentProps: {
          trophyType: data.trophyType
        },
        cssClass: 'present-modal'
      })
      .pipe(
        take(1),
        switchMap(() => {
          const payload: MarkNotificationsAsReadBody = { notificationIds: [data.notificationId] };

          return this.notificationsService.markAsRead(payload);
        })
      )
      .subscribe();
  }

  private showToast(message: string): void {
    this.toastProvider.showToast({
      message: message,
      position: 'top',
      cssClass: ['or-d-flex or-text-align-center or-text-lg or-color-white or-mt-12-px or-toast-style']
    });
  }
}
